import React, { useState } from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'

// Number of elements preloaded before and after the visible area
const PRELOADED_ELEMENTS_OUTSIDE_VIEWPORT = 10

export enum VirtualizationTypes {
  VARIABLE = 'variable',
  FIXED = 'fixed',
}

interface VirtualizationProps<T> {
  items: T[]
  defaultItemHeight: number
  getHeight?: (index: number) => number | null | undefined
  containerRef: React.RefObject<HTMLElement>
  shadowHeight?: number
  preloadedElementsOutsideViewport?: number
  type?: VirtualizationTypes
}

interface Return {
  visibleStartIndex: number
  endIndex: number
  topPlaceholderHeight: number
  bottomPlaceholderHeight: number
  scrollTopShadow: boolean
  scrollBottomShadow: boolean
}

function useVirtualization<T>({
  items,
  defaultItemHeight,
  getHeight,
  containerRef,
  shadowHeight,
  preloadedElementsOutsideViewport = PRELOADED_ELEMENTS_OUTSIDE_VIEWPORT,
  type = VirtualizationTypes.FIXED,
}: VirtualizationProps<T>): Return {
  // This containerRef is going to contain the scrollable container, not nullable
  const containerHeight = containerRef.current?.clientHeight || window.innerHeight
  const [visibleStartIndex, setVisibleStartIndex] = useState(0)
  const [scrollTopShadow, setScrollTopShadow] = useState(false)
  const [scrollBottomShadow, setScrollBottomShadow] = useState(false)
  let animationFrameId: number | null = null

  const countItemsInView = (startIndex: number, maxHeight: number): number => {
    if (type === VirtualizationTypes.FIXED) {
      return Math.ceil(maxHeight / defaultItemHeight)
    }

    let itemsHeight = 0
    let itemsInView = 0
    for (let i = startIndex; itemsHeight < maxHeight; i++) {
      const height = getHeight && getHeight(i)
      itemsHeight += height || defaultItemHeight
      itemsInView++
    }
    return itemsInView
  }

  const calculateShadows = (
    scrollTop: number,
    scrollBottom: number,
    scrollShadowHeight: number
  ) => {
    setScrollTopShadow(scrollTop > scrollShadowHeight)
    setScrollBottomShadow(scrollBottom > scrollShadowHeight)
  }

  const handleScroll = () => {
    if (animationFrameId) cancelAnimationFrame(animationFrameId)

    animationFrameId = requestAnimationFrame(() => {
      if (containerRef.current) {
        const scrollTop = containerRef.current.scrollTop

        // Calculate the start index of visible items based on scroll position.
        const startIndex = Math.max(
          0,
          countItemsInView(0, scrollTop) - preloadedElementsOutsideViewport
        )

        setVisibleStartIndex(startIndex)

        // Shadows calculations
        if (shadowHeight) {
          const scrollBottom =
            containerRef.current.scrollHeight -
            containerRef.current.scrollTop -
            containerRef.current.clientHeight
          calculateShadows(scrollTop, scrollBottom, shadowHeight)
        }
      }

      animationFrameId = null
    })
  }

  const calculateHeight = (startIndex: number, endIndex: number): number => {
    if (type === VirtualizationTypes.FIXED) {
      return endIndex * defaultItemHeight
    }

    let totalHeight = 0
    for (let i = startIndex; i < endIndex; i++) {
      const height = getHeight && getHeight(i)
      totalHeight += height || defaultItemHeight
    }
    return totalHeight
  }

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)

      if (shadowHeight) {
        const scrollTop = container.scrollTop
        const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight
        calculateShadows(scrollTop, scrollBottom, shadowHeight)
      }
      return () => {
        animationFrameId && cancelAnimationFrame(animationFrameId)
        container.removeEventListener('scroll', handleScroll)
      }
    }

    // Keep this dependency to refresh the listener and avoid desync with the shiftsRows array
    // Otherwise, it performs the handleScroll with a page back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  const totalHeight = calculateHeight(0, items.length)
  // Calculates the number of items that should be rendered in the viewport
  // preloadedElementsOutsideViewport * 2 takes into account additional items above and beyond for smoothness when scrolling
  const visibleItemCount =
    countItemsInView(visibleStartIndex, containerHeight) + preloadedElementsOutsideViewport * 2

  // Calculate heights for top and bottom placeholders.
  const endIndex = Math.min(items.length, visibleStartIndex + visibleItemCount)
  const topPlaceholderHeight = calculateHeight(0, visibleStartIndex)
  const bottomPlaceholderHeight = totalHeight - calculateHeight(0, endIndex)

  return {
    visibleStartIndex,
    endIndex,
    topPlaceholderHeight,
    bottomPlaceholderHeight,
    scrollTopShadow,
    scrollBottomShadow,
  }
}

export default useVirtualization
